import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Grid, makeStyles, Box } from '@material-ui/core';
import { z } from 'zod';
import SalesStreamDropdown from '../components/Dropdown';
import { useQuerySalesStreamFilters } from '../../../hooks';
import { getUserFromEmail } from '../../../helpers/user';
import { SS_FILTER_ORGANIZATIONS } from '../../../helpers/constants';
import SwitchInput from '../components/SwitchInput';
import NumberInput from '../components/NumberInput';
import { hasCotaAtFloorAccess } from '../../../helpers/sales-stream';

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;

const numberValidator = z
  .string()
  .transform(val => (val === '' ? null : Number(val)))
  .pipe(
    z
      .number()
      .int({ message: 'Value must be an integer' })
      .min(MIN_PERCENTAGE, { message: `Value must be at least ${MIN_PERCENTAGE}` })
      .max(MAX_PERCENTAGE, { message: `Value must not exceed ${MAX_PERCENTAGE}` })
      .nullable()
  );

const useStyles = makeStyles({
  container: {
    padding: '0 10px',
  },
});

function useDebouncedCallback(callback, delay) {
  const timeoutIDRef = useRef(null);
  const savedCallback = useRef();
  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutIDRef.current) {
        clearTimeout(timeoutIDRef.current);
      }

      timeoutIDRef.current = setTimeout(() => {
        // Call the latest callback
        savedCallback.current(...args);
      }, delay);
    },
    [delay]
  );

  // Save the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  return debouncedCallback;
}

const SalesStreamFilters = ({
  isFilterByVisible,
  handleFilterChange,
  filterValue,
  cotaAtFloorPercentage: initialCotaAtFloorPercentage,
  onCotaAtFloorPercentageChange,
}) => {
  const classes = useStyles();
  const { data, isFetching } = useQuerySalesStreamFilters();
  const [cotaAtFloorEnabled, setCotaAtFloorEnabled] = useState(initialCotaAtFloorPercentage !== null);
  const [cotaAtFloorPercentage, setCotaAtFloorPercentage] = useState(
    initialCotaAtFloorPercentage !== null ? initialCotaAtFloorPercentage.toString() : ''
  );
  const [cotaAtFloorPercentageError, setCotaAtFloorPercentageError] = useState('');
  const showCotaAtFloorPercentageFilter = hasCotaAtFloorAccess();

  const handleCotaAtFloorSwitchChange = isEnabled => {
    setCotaAtFloorEnabled(isEnabled);

    if (!isEnabled) {
      setCotaAtFloorPercentage('');
      setCotaAtFloorPercentageError('');
      onCotaAtFloorPercentageChange(null);
    }
  };

  const validateAndNotifyParent = useDebouncedCallback(value => {
    const validationResult = numberValidator.safeParse(value);

    // If the value is invalid, show the error message
    if (!validationResult.success) {
      setCotaAtFloorPercentageError(validationResult.error.issues[0].message);
      return;
    }

    onCotaAtFloorPercentageChange(validationResult.data);
  }, 500);

  const handleCotaAtFloorPercentageChange = value => {
    setCotaAtFloorPercentage(value);
    setCotaAtFloorPercentageError('');
    validateAndNotifyParent(value);
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      {isFilterByVisible ? (
        <Grid item xs={12}>
          {isFetching ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress size={37} />
            </Box>
          ) : (
            <SalesStreamDropdown
              label="Filter by"
              defaultValue={getUserFromEmail()}
              selectedValue={filterValue}
              pricingAnalystItems={(data && data.pricing_analyst_filters) || []}
              marketAnalystItems={(data && data.market_analyst_filters) || []}
              teamsItems={(data && data.pricing_team_filters) || []}
              organizations={SS_FILTER_ORGANIZATIONS}
              onChange={handleFilterChange}
            />
          )}
        </Grid>
      ) : null}

      {showCotaAtFloorPercentageFilter ? (
        <>
          <Grid item xs={12}>
            <SwitchInput
              id="cota-at-floor"
              label="% COTA at Floor"
              checked={cotaAtFloorEnabled}
              onChange={value => handleCotaAtFloorSwitchChange(value)}
            />
          </Grid>
          {cotaAtFloorEnabled ? (
            <Grid item xs={12}>
              <NumberInput
                id="cota-at-floor-percentage"
                label="Add %"
                min={MIN_PERCENTAGE}
                max={MAX_PERCENTAGE}
                error={cotaAtFloorPercentageError}
                value={cotaAtFloorPercentage}
                onChange={value => handleCotaAtFloorPercentageChange(value)}
              />
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};

SalesStreamFilters.defaultProps = {
  filterValue: null,
  cotaAtFloorPercentage: null,
};

SalesStreamFilters.propTypes = {
  isFilterByVisible: PropTypes.bool.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  filterValue: PropTypes.array,
  cotaAtFloorPercentage: PropTypes.number,
  onCotaAtFloorPercentageChange: PropTypes.func.isRequired,
};

export default SalesStreamFilters;
